import { createContext, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { isTopModalLayer, registerModalLayer } from './modalLayers'

export const ModalPortalContext = createContext<HTMLElement | null>(null)

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  headerActions?: React.ReactNode
  subtleBackdrop?: boolean
}

export default function Modal({ isOpen, onClose, title, children, headerActions, subtleBackdrop = false }: ModalProps) {
  const [portal] = useState(() => document.createElement('div'))
  const dialog = useRef<HTMLDivElement>(null)
  const close = useRef(onClose)
  close.current = onClose

  useLayoutEffect(() => {
    if (!isOpen) return
    document.body.appendChild(portal)
    const unregister = registerModalLayer(portal)
    const focusables = () => Array.from(portal.querySelectorAll<HTMLElement>(
      'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]',
    ) ?? []).filter(el => !el.closest('[inert]') && !el.hidden)
    const focusFirst = () => (focusables()[0] ?? dialog.current)?.focus()
    focusFirst()
    const keydown = (event: KeyboardEvent) => {
      if (!isTopModalLayer(portal)) return
      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopImmediatePropagation()
        close.current()
      }
      if (event.key === 'Tab') {
        const items = focusables()
        const index = items.indexOf(document.activeElement as HTMLElement)
        if (!items.length || (event.shiftKey ? index <= 0 : index === items.length - 1 || index < 0)) {
          event.preventDefault()
          ;(event.shiftKey ? items[items.length - 1] : items[0])?.focus()
        }
      }
    }
    const focusin = (event: FocusEvent) => {
      if (isTopModalLayer(portal) && !portal.contains(event.target as Node)) focusFirst()
    }
    document.addEventListener('keydown', keydown, true)
    document.addEventListener('focusin', focusin)
    return () => {
      document.removeEventListener('keydown', keydown, true)
      document.removeEventListener('focusin', focusin)
      portal.remove()
      unregister()
    }
  }, [isOpen, portal])

  if (!isOpen) return null
  return createPortal(
    <ModalPortalContext.Provider value={portal}>
      <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 'inherit' }}>
        <div className={`absolute inset-0 ${subtleBackdrop ? 'bg-charcoal/20' : 'bg-[#351E1D]/50 backdrop-blur-sm'}`} data-modal-backdrop
          onClick={() => { if (isTopModalLayer(portal)) onClose() }} />
        <div ref={dialog} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1}
          style={{ transform: 'translateY(var(--modal-offset, 0px))', maxHeight: 'calc(90vh - var(--modal-offset, 0px))' }}
          className="relative bg-white rounded-card shadow-elevated w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4 border-b border-stone/20">
            <h2 className="text-lg font-semibold text-text-primary font-sans">{title}</h2>
            <div className="flex items-center gap-2 ml-auto">
              {headerActions}
              <button type="button" onClick={onClose} aria-label={`Close ${title}`}
                className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full hover:bg-stone/20 text-text-secondary hover:text-text-primary transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
          <div className="px-6 py-4">{children}</div>
        </div>
      </div>
    </ModalPortalContext.Provider>, portal,
  )
}
